import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardSelector from './DashboardSelector';
import { useDashboardContext } from '../hooks/useDashboardContext';
import { useDashboardPopup } from '../contexts/DashboardPopupContext';
import { formatValue as formatNumber } from '../utils/numberFormatter';
import './TenantHeader.css';

// AdminPage를 파일 상단에서 lazy 로드하여 컴포넌트 아이덴티티를 안정화
const LazyAdminPage = React.lazy(() => import('../pages/AdminPage'));

// API 기본 URL 설정
const API_URL = process.env.REACT_APP_API_URL || '/api';
const POPUP_DASHBOARD_ID = 'dashboard_2026-02-08T05-21-44';

const TenantHeader = ({ onUserInfoChange, onTenantChange, onDashboardTitleChange }) => {
  const navigate = useNavigate();
  const { openDashboard } = useDashboardPopup();
  const [currentTenant, setCurrentTenant] = useState('');
  const [availableTenants, setAvailableTenants] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tokenQuota, setTokenQuota] = useState(null);
  const [tokenInfoOpen, setTokenInfoOpen] = useState(false);
  const [userInfo, setUserInfo] = useState(null); // 사용자 정보 상태 추가
  const [showAdminPage, setShowAdminPage] = useState(false); // 관리자 페이지 표시 상태
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 }); // 팝업 위치 상태

  // 비밀번호 변경 모달 상태
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(false); // 최초 로그인 강제 변경
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const dropdownRef = useRef(null);
  const tokenInfoRef = useRef(null);
  // 전역 디버그 사용 여부 (관리자 체크박스)
  const [debugEnabled, setDebugEnabled] = useState(() => {
    try {
      return localStorage.getItem('enerwiser.debug.globalVariables') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [debugVisible, setDebugVisible] = useState(() => {
    try {
      return localStorage.getItem('enerwiser.debug.globalVariables.visible') !== 'false';
    } catch (e) {
      return true;
    }
  });

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'enerwiser.debug.globalVariables.visible') {
        setDebugVisible(e.newValue !== 'false');
      } else if (e.key === 'enerwiser.debug.globalVariables') {
        setDebugEnabled(e.newValue === 'true');
      }
    };
    const onCustom = (e) => {
      if (typeof e.detail === 'boolean') setDebugVisible(e.detail);
    };
    const onEnabled = (e) => {
      if (typeof e.detail === 'boolean') setDebugEnabled(e.detail);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('enerwiser:debug-panel-visibility', onCustom);
    window.addEventListener('enerwiser:debug-globalVariables', onEnabled);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('enerwiser:debug-panel-visibility', onCustom);
      window.removeEventListener('enerwiser:debug-globalVariables', onEnabled);
    };
  }, []);

  // 최초 로그인 시 비밀번호 변경 강제 모달
  useEffect(() => {
    const mustChange = sessionStorage.getItem('must_change_password');
    if (mustChange === 'true') {
      setForcePasswordChange(true);
      setShowPasswordModal(true);
    }
  }, []);

  const toggleDebugPanelFromHeader = () => {
    const next = !debugVisible;
    setDebugVisible(next);
    try {
      localStorage.setItem('enerwiser.debug.globalVariables.visible', next ? 'true' : 'false');
      window.dispatchEvent(new CustomEvent('enerwiser:debug-panel-visibility', { detail: next }));
    } catch (e) {
      // noop
    }
  };
  
  // 사용자 정보 가져오기 함수 - API 호출로 변경
  const fetchUserInfo = useCallback(async () => {
    try {
      // /api/users/me/ API 호출로 변경
      const response = await axios.get(`${API_URL}/users/me/`, {
        withCredentials: true
      });
      
      if (response.data) {
        const userData = {
          id: response.data.id,
          username: response.data.username,
          email: response.data.email,
          is_staff: response.data.is_staff,
          is_superuser: response.data.is_superuser
        };
        
        setUserInfo(userData);
        
        // 콜백을 통해 부모 컴포넌트에 사용자 정보 전달
        if (onUserInfoChange) {
          onUserInfoChange(userData);
        }
      } else {
        setUserInfo(null);
        if (onUserInfoChange) {
          onUserInfoChange(null);
        }
      }
    } catch (error) {
      // API 호출 실패 시 userInfo를 null로 설정
      setUserInfo(null);
      if (onUserInfoChange) {
        onUserInfoChange(null);
      }
    }
  }, [onUserInfoChange]);

  // fetchTokenQuota 함수를 useCallback으로 컴포넌트 범위에서 정의
  const fetchTokenQuota = useCallback(async () => {
    if (!currentTenant) return; // 현재 테넌트가 없으면 실행하지 않음
    
    try {
      // 토큰 할당량 API 호출을 직접 axios로 변경
      const response = await axios.get(`${API_URL}/usage/token-quota/`, {
        withCredentials: true
      });
      
      if (response.data) {
        // 상태 변경 최적화: 값이 실제로 변경되었을 때만 업데이트
        setTokenQuota(prevQuota => {
          // 이전 값과 비교
          if (prevQuota && 
              prevQuota.used_tokens === response.data.used_tokens &&
              prevQuota.remaining_tokens === response.data.remaining_tokens &&
              prevQuota.usage_percentage === response.data.usage_percentage) {
            return prevQuota; // 값이 동일하면 상태 업데이트하지 않음
          }
          
          return response.data;
        });
      }
    } catch (error) {
    }
  }, [currentTenant]);
  
  // 페이지 로드 시 사용자가 액세스 가능한 테넌트 목록과 현재 테넌트 가져오기
  useEffect(() => {
    const fetchTenantInfo = async () => {
      try {
        // 테넌트 목록 API 호출
        const response = await axios.get(`${API_URL}/users/tenants/`, {
          withCredentials: true
        });
        
        if (response.data && response.data.tenants && response.data.tenants.length > 0) {
          setAvailableTenants(response.data.tenants);
        }
        
        // 현재 설정된 테넌트 확인
        const tenantResponse = await axios.get(`${API_URL}/debug/tenant/`, {
          withCredentials: true
        });
        
        if (tenantResponse.data.current_tenant) {
          setCurrentTenant(tenantResponse.data.current_tenant);
          
          // 콜백을 통해 부모 컴포넌트에 테넌트 정보 전달
          if (onTenantChange) {
            onTenantChange(tenantResponse.data.current_tenant);
          }
        }

        // 사용자 정보 가져오기 - API 호출로 변경
        fetchUserInfo();
      } catch (error) {
        if (error.response && error.response.status === 401) {
        } else {
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchTenantInfo();
  }, [navigate, fetchUserInfo, onTenantChange]);

  // 페이지 로드 시 사용자의 토큰 할당량 가져오기 및 업데이트 리스너 등록
  useEffect(() => {
    // 사용자가 로그인했을 때만 토큰 할당량 조회
    if (!loading && currentTenant) {
      // 초기 데이터 로드
      fetchTokenQuota();
      
      // 30초마다 토큰 사용량 업데이트 (주기적 폴링)
      // 상태 변경 최적화로 값이 변경되지 않으면 재렌더링되지 않음
      const tokenUpdateInterval = setInterval(() => {
        fetchTokenQuota();
      }, 30000); // 30초 간격으로 설정
      
      // 컴포넌트 언마운트 시 인터벌과 리스너 클리어
      return () => {
        clearInterval(tokenUpdateInterval);
      };
    }
  }, [loading, currentTenant, fetchTokenQuota]); // showAdminPage 의존성 제거
  
  // 팝업 위치 업데이트
  useEffect(() => {
    if (tokenInfoOpen && tokenInfoRef.current) {
      const updatePosition = () => {
        const rect = tokenInfoRef.current.getBoundingClientRect();
        setPopupPosition({
          top: rect.bottom + 5,
          left: rect.left,
        });
      };
      
      updatePosition();
      
      // 스크롤이나 리사이즈 시에도 위치 업데이트
      const handleUpdate = () => updatePosition();
      window.addEventListener('scroll', handleUpdate, { passive: true, capture: true });
      window.addEventListener('resize', handleUpdate, { passive: true });
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [tokenInfoOpen]);

  // 클릭 이벤트 감지하여 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      
      // Portal로 렌더링된 토큰 팝업 외부 클릭 감지
      if (tokenInfoOpen) {
        const tokenPortal = document.querySelector('.token-usage-details-portal');
        if (tokenInfoRef.current && !tokenInfoRef.current.contains(event.target) && 
            (!tokenPortal || !tokenPortal.contains(event.target))) {
          setTokenInfoOpen(false);
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [tokenInfoOpen]);
  
  // 관리자 화면 버튼은 슈퍼관리자만 노출
  const isSuperAdmin = useMemo(() => {
    return userInfo && userInfo.is_superuser;
  }, [userInfo]);

  // 관리자 페이지 이동 함수
  const handleAdminPage = () => {
    setShowAdminPage(true);
  };

  // 현재 로드된 대시보드 ID를 컨텍스트에서 획득
  const { dashboardTemplate } = useDashboardContext();
  const getCurrentDashboardId = () => dashboardTemplate?.dashboard_id || dashboardTemplate?.dashboardId || null;

  const handleOpenPopupDashboard = useCallback(() => {
    if (!POPUP_DASHBOARD_ID) {
      console.warn('[TenantHeader] 팝업 대시보드 ID가 설정되지 않았어.');
      return;
    }

    try {
      openDashboard({
        dashboardId: POPUP_DASHBOARD_ID,
        dashboardName: '팝업 대시보드',
        title: '팝업 대시보드',
        sizeMode: 'auto',
        position: 'center',
        options: {
          closeOnEscape: true,
          draggable: true,
          resizable: true
        }
      });
    } catch (error) {
      console.warn('[TenantHeader] 팝업 대시보드 열기 실패', error);
      alert('팝업 대시보드를 여는 중 문제가 발생했어. 다시 시도해줘.');
    }
  }, [openDashboard]);

  // 메인 페이지로 돌아가기 함수
  const handleBackToMain = () => {
    setShowAdminPage(false);
  };

  // 테넌트 선택 처리
  const handleTenantChange = async (tenantId) => {
    try {
      // 테넌트 선택 API 호출
      const response = await axios.post(
        `${API_URL}/users/select-tenant/`,
        { tenant_name: tenantId },
        { withCredentials: true }
      );
      
      if (response.status === 200) {
        // 상태 업데이트
        setCurrentTenant(tenantId);
        
        // 콜백을 통해 부모 컴포넌트에 테넌트 정보 전달
        if (onTenantChange) {
          onTenantChange(tenantId);
        }
        
        // 드롭다운 닫기
        setDropdownOpen(false);
        
        // 페이지 새로고침 (테넌트 전환 완료)
        window.location.reload();
      }
    } catch (error) {
    }
  };
  
  // 로그아웃 처리 함수 추가
  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/users/logout/`, {}, {
        withCredentials: true,
      });
      localStorage.removeItem('token');
      window.location.href = '/';
    } catch (error) {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
  };

  // 비밀번호 변경 모달 열기
  const openPasswordModal = () => {
    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordSuccess('');
    setShowPasswordModal(true);
  };

  // 비밀번호 변경 모달 닫기
  const closePasswordModal = () => {
    // 최초 로그인 강제 변경 모드에서는 닫기 차단
    if (forcePasswordChange) {
      setPasswordError('보안을 위해 비밀번호를 변경해야 합니다.');
      return;
    }
    setShowPasswordModal(false);
    setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setPasswordError('');
    setPasswordSuccess('');
  };

  // 비밀번호 변경 처리
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // 유효성 검사
    if (!passwordForm.oldPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('모든 필드를 입력해주세요.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('새 비밀번호가 일치하지 않습니다.');
      return;
    }

    if (passwordForm.newPassword.length < 9) {
      setPasswordError('새 비밀번호는 최소 9자 이상이어야 합니다.');
      return;
    }

    // 비밀번호 복잡도 검증: 영대문자, 영소문자, 숫자, 특수문자 중 3종류 이상
    const pw = passwordForm.newPassword;
    let charTypes = 0;
    if (/[A-Z]/.test(pw)) charTypes++;
    if (/[a-z]/.test(pw)) charTypes++;
    if (/[0-9]/.test(pw)) charTypes++;
    if (/[!@#$%^&*()_+\-=\[\]{}|;:'",.<>?/`~\\]/.test(pw)) charTypes++;
    if (charTypes < 3) {
      setPasswordError('비밀번호는 영대문자, 영소문자, 숫자, 특수문자 중 3종류 이상을 포함해야 합니다.');
      return;
    }

    setPasswordLoading(true);

    try {
      const response = await axios.post(`${API_URL}/users/change-password/`, {
        old_password: passwordForm.oldPassword,
        new_password: passwordForm.newPassword,
        new_password2: passwordForm.confirmPassword
      }, {
        withCredentials: true
      });

      if (response.status === 200) {
        setPasswordSuccess('비밀번호가 성공적으로 변경되었습니다.');
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
        // 최초 로그인 강제 변경 플래그 해제
        if (forcePasswordChange) {
          sessionStorage.removeItem('must_change_password');
          setForcePasswordChange(false);
        }
        // 2초 후 모달 닫기
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordError('');
          setPasswordSuccess('');
        }, 2000);
      }
    } catch (error) {
      if (error.response?.data?.old_password) {
        setPasswordError(error.response.data.old_password[0]);
      } else if (error.response?.data?.new_password) {
        setPasswordError(error.response.data.new_password[0]);
      } else {
        setPasswordError('비밀번호 변경에 실패했습니다. 다시 시도해주세요.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };
  
  // 현재 테넌트 객체 찾기
  const getCurrentTenantInfo = () => {
    if (!currentTenant || availableTenants.length === 0) {
      return { tenant_name: '', display_name: loading ? '로딩 중...' : '테넌트 선택' };
    }
    
    const tenant = availableTenants.find(t => t.tenant_name === currentTenant);
    
    if (tenant) {
      return {
        tenant_name: tenant.tenant_name,
        display_name: tenant.tenant_name === 'ess' ? 'ESS' : 
                     tenant.tenant_name === 'evcheck' ? 'EVCheck' : 
                     tenant.tenant_name
      };
    }
    
    return {
      tenant_name: currentTenant,
      display_name: currentTenant === 'ess' ? 'ESS' : 
                   currentTenant === 'evcheck' ? 'EVCheck' : 
                   currentTenant
    };
  };
  
  // 테넌트 아이콘 렌더링
  const renderTenantIcon = (tenantId) => {
    if (tenantId === 'ess') {
      return <span role="img" aria-label="battery">🔋</span>;
    } else if (tenantId === 'evcheck') {
      return <span role="img" aria-label="car">🚗</span>;
    }
    return <span>🏢</span>;
  };

  // 토큰 사용량 정보 렌더링
  const renderTokenUsage = () => {
    // 관리자 페이지에서는 토큰 정보를 표시하지 않음
    if (showAdminPage || !tokenQuota) return null;
    
    // 사용량 백분율 계산
    const usagePercentage = tokenQuota.usage_percentage;
    
    // 사용량에 따른 색상 설정
    let usageColor = 'var(--color-success)'; // 기본 색상 (낮은 사용량)
    
    if (tokenQuota.is_over_quota || usagePercentage > 100) {
      usageColor = '#FF4444'; // 할당량 초과 (빨간색)
    } else if (usagePercentage > 90) {
      usageColor = 'var(--color-danger)'; // 매우 높은 사용량
    } else if (usagePercentage > 70) {
      usageColor = 'var(--color-warning)'; // 높은 사용량
    }
    
    // 토큰 수 포맷은 공통 유틸리티 함수 사용

    
    return (
      <>
        <div className="token-usage-container" ref={tokenInfoRef}>
          <div 
            className="token-usage-display" 
            onClick={() => setTokenInfoOpen(!tokenInfoOpen)}
          >
            <span role="img" aria-label="token">🪙</span>
            <div className="token-progress">
              <div 
                className="token-progress-bar" 
                style={{ 
                  width: `${Math.min(usagePercentage, 100)}%`, 
                  backgroundColor: usageColor 
                }}
              ></div>
            </div>
            <span className="token-percentage" style={{ color: tokenQuota.is_over_quota ? '#FF4444' : '#495057', fontWeight: tokenQuota.is_over_quota ? 'bold' : 'normal' }}>
              {usagePercentage.toFixed(1)}%{tokenQuota.is_over_quota ? ' ⚠️' : ''}
            </span>
          </div>
        </div>
        
        {/* Portal을 사용하여 팝업을 body에 직접 렌더링 */}
        {tokenInfoOpen && createPortal(
          <div 
            className="token-usage-details-portal"
            style={{
              position: 'fixed',
              top: `${popupPosition.top}px`,
              left: `${popupPosition.left}px`,
              backgroundColor: 'white',
              borderRadius: '6px',
              boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
              width: '250px',
              padding: '15px',
              zIndex: 999999,
              border: '1px solid #e9ecef',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 style={{ 
              margin: '0 0 10px 0', 
              fontSize: '16px', 
              color: '#212529', 
              borderBottom: '1px solid #e9ecef', 
              paddingBottom: '8px' 
            }}>
              ENERWISER 사용 가능량
            </h4>
            {tokenQuota.is_over_quota && (
              <div style={{ 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7', 
                borderRadius: '4px', 
                padding: '8px', 
                marginBottom: '10px',
                fontSize: '12px',
                color: '#856404'
              }}>
                ⚠️ 할당량 초과: 추가 토큰이 필요할 수 있습니다.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
              <span style={{ color: '#6c757d' }}>할당된 토큰:</span>
              <span style={{ fontWeight: '500', color: '#212529' }}>{formatNumber(tokenQuota.allocated_tokens)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
              <span style={{ color: '#6c757d' }}>사용된 토큰:</span>
              <span style={{ 
                color: tokenQuota.is_over_quota ? '#FF4444' : '#212529', 
                fontWeight: tokenQuota.is_over_quota ? 'bold' : '500' 
              }}>
                {formatNumber(tokenQuota.used_tokens)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
              <span style={{ color: '#6c757d' }}>남은 토큰:</span>
              <span style={{ 
                color: tokenQuota.remaining_tokens <= 0 ? '#FF4444' : '#212529',
                fontWeight: '500'
              }}>
                {formatNumber(Math.max(0, tokenQuota.remaining_tokens))}
              </span>
            </div>
            {tokenQuota.token_type && (
              <div style={{ marginBottom: '8px' }}>
                <small style={{ color: '#666', fontSize: '0.85em' }}>
                  💡 {tokenQuota.description || '가중 토큰 방식 적용'}
                </small>
              </div>
            )}
            {tokenQuota.expiry_date && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                <span style={{ color: '#6c757d' }}>만료일:</span>
                <span style={{ fontWeight: '500', color: '#212529' }}>
                  {new Date(tokenQuota.expiry_date).toLocaleDateString()}
                </span>
              </div>
            )}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                fetchTokenQuota();
              }}
              style={{
                width: '100%',
                backgroundColor: '#f0f2f5',
                border: '1px solid #e2e8f0',
                color: '#4b5563',
                borderRadius: '4px',
                padding: '6px 0',
                marginTop: '10px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease'
              }}
              onMouseOver={(e) => { e.target.style.backgroundColor = '#e2e8f0'; }}
              onMouseOut={(e) => { e.target.style.backgroundColor = '#f0f2f5'; }}
            >
              새로고침
            </button>
          </div>,
          document.body
        )}
      </>
    );
  };

  if (loading) {
    return null;
  }

  if (!currentTenant && !loading) {
     return null;
  }

  // 관리자 페이지가 표시될 때는 별도 컴포넌트를 렌더링
  if (showAdminPage) {
    return (
      <React.Suspense fallback={
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div>관리자 페이지를 로딩 중...</div>
        </div>
      }>
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          zIndex: 9999,
          overflow: 'auto'
        }}>
          {/* 뒤로가기 버튼 */}
          <div style={{ 
            position: 'absolute', 
            top: '20px', 
            left: '20px', 
            zIndex: 99999
          }}>
            <button
              onClick={handleBackToMain}
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                border: '2px solid #667eea',
                borderRadius: '12px',
                padding: '12px 24px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: '#667eea',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                backdropFilter: 'blur(10px)',
                minWidth: '160px',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#667eea';
                e.target.style.color = 'white';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.95)';
                e.target.style.color = '#667eea';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
              }}
            >
              ← 메인으로 돌아가기
            </button>
          </div>
          <LazyAdminPage />
        </div>
      </React.Suspense>
    );
  }

  return (
    <div className="tenant-header">
      <div 
        className="tenant-selector" 
        onClick={() => setDropdownOpen(!dropdownOpen)}
        ref={dropdownRef}
      >
        <div className="current-tenant">
          <div className="tenant-icon-small">
            {renderTenantIcon(getCurrentTenantInfo().tenant_name)}
          </div>
          <span className="tenant-name-display">
            {getCurrentTenantInfo().display_name}
          </span>
          <span className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>▼</span>
        </div>
        
        {dropdownOpen && (
          <div className="tenant-dropdown">
            {availableTenants.map((tenant) => (
              <div 
                key={tenant.tenant_name}
                className={`tenant-option ${currentTenant === tenant.tenant_name ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleTenantChange(tenant.tenant_name);
                }}
              >
                <div className="tenant-icon-small">
                  {renderTenantIcon(tenant.tenant_name)}
                </div>
                <div className="tenant-option-info">
                  <span className="tenant-option-name">
                    {tenant.tenant_name === 'ess' ? 'ESS' : 
                     tenant.tenant_name === 'evcheck' ? 'EVCheck' : 
                     tenant.tenant_name}
                  </span>
                  <span className="tenant-option-desc">
                    {tenant.tenant_name === 'ess' ? 'Energy Storage System' : 
                     tenant.tenant_name === 'evcheck' ? '전기차 배터리 진단' : 
                     '서비스'}
                    {tenant.is_default && ' (기본)'}
                  </span>
                </div>
                {currentTenant === tenant.tenant_name && <span className="check-mark">✓</span>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 토큰 사용량 표시 추가 */}
      {renderTokenUsage()}

      {/* 대시보드 선택 컴포넌트 추가 */}
      <DashboardSelector 
        className="header-dashboard-selector"
        onDashboardTitleChange={onDashboardTitleChange}
      />

      {/* 인쇄 버튼 - 숨김 처리 */}
      {/* <div style={{ display: 'flex', gap: '8px', marginRight: '10px' }}>
        <button
          onClick={() => {
            const id = getCurrentDashboardId();
            // snapshot current dashboard container width in pixels for print page
            try {
              const container = document.querySelector('.dashboard-container');
              const containerWidthPx = container ? Math.round(container.getBoundingClientRect().width) : null;
              sessionStorage.setItem('enerwiser.print.snapshot', JSON.stringify({ containerWidthPx }));
            } catch (_) {}
            // 하나의 인쇄 페이지로 열고, 가로/세로는 인쇄창에서 선택
            navigate(`/print${id ? `?id=${encodeURIComponent(id)}` : ''}`);
          }}
          className="admin-button"
          style={{
            background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
            color: 'white',
            border: 'none',
            padding: '8px 12px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            boxShadow: '0 3px 10px rgba(2, 132, 199, 0.25)'
          }}
          title="인쇄 미리보기 (가로/세로는 인쇄창에서 선택)"
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = '0 5px 14px rgba(2, 132, 199, 0.35)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 3px 10px rgba(2, 132, 199, 0.25)';
          }}
        >
          🖨️ 인쇄
        </button>
      </div> */}

      {/* 관리자 버튼: 슈퍼관리자만 노출 - 숨김 처리 */}
      {/* {isSuperAdmin && (
        <button
          onClick={handleAdminPage}
          className="admin-button"
          style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
            marginRight: '10px'
          }}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
          }}
        >
          🔧 관리자
        </button>
      )} */}

      {/* 디버그 아이콘: 관리자 디버그 모드 켜짐일 때만 노출 */}
      {debugEnabled && (
        <button 
          onClick={toggleDebugPanelFromHeader}
          className="admin-button"
          style={{
            background: 'linear-gradient(135deg, #2b8a3e, #2f9e44)',
            color: 'white',
            border: 'none',
            padding: '10px 12px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 15px rgba(47, 158, 68, 0.3)',
            marginRight: '10px'
          }}
          title={debugVisible ? '변수창 숨기기' : '디버그 패널 열기'}
          onMouseOver={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 6px 20px rgba(47, 158, 68, 0.4)';
          }}
          onMouseOut={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 4px 15px rgba(47, 158, 68, 0.3)';
          }}
        >
          🐞 {debugVisible ? '변수창 숨기기' : '변수창 열기'}
        </button>
      )}

      <button
        onClick={handleOpenPopupDashboard}
        className="admin-button"
        style={{
          background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
          color: 'white',
          border: 'none',
          padding: '10px 12px',
          borderRadius: '12px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
          marginRight: '8px'
        }}
        onMouseOver={(e) => {
          e.target.style.transform = 'translateY(-2px)';
          e.target.style.boxShadow = '0 6px 20px rgba(37, 99, 235, 0.4)';
        }}
        onMouseOut={(e) => {
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 4px 15px rgba(37, 99, 235, 0.3)';
        }}
      >
        📊 LIVE
      </button>

      {/* 비밀번호 변경 버튼 */}
      <button
        onClick={openPasswordModal}
        className="password-change-button"
        style={{
          background: 'transparent',
          color: '#6c757d',
          border: '1px solid #dee2e6',
          padding: '8px 10px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          marginRight: '8px'
        }}
        title="비밀번호 변경"
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#f8f9fa';
          e.currentTarget.style.borderColor = '#adb5bd';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = '#dee2e6';
        }}
      >
        🔑
      </button>

      <button
        onClick={() => { window.location.href = '/select-tenant'; }}
        style={{
          background: 'transparent',
          color: '#6c757d',
          border: '1px solid #dee2e6',
          padding: '8px 10px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          marginRight: '8px'
        }}
        title="서비스 선택으로 이동"
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#f8f9fa';
          e.currentTarget.style.borderColor = '#adb5bd';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = '#dee2e6';
        }}
      >
        🏠
      </button>

      <button onClick={handleLogout} className="logout-button">
        로그아웃
      </button>

      {/* 비밀번호 변경 모달 */}
      {showPasswordModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999
          }}
          onClick={closePasswordModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '400px',
              maxWidth: '90%',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#212529' }}>
              비밀번호 변경
            </h3>
            {forcePasswordChange && (
              <div style={{
                backgroundColor: '#fff3cd',
                color: '#856404',
                padding: '10px 12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '13px',
                border: '1px solid #ffc107'
              }}>
                보안을 위해 초기 비밀번호를 변경해야 합니다. 비밀번호를 변경한 후 서비스를 이용할 수 있습니다.
              </div>
            )}

            {passwordError && (
              <div style={{
                backgroundColor: '#f8d7da',
                color: '#721c24',
                padding: '10px 12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div style={{
                backgroundColor: '#d4edda',
                color: '#155724',
                padding: '10px 12px',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px'
              }}>
                {passwordSuccess}
              </div>
            )}

            <form onSubmit={handlePasswordChange}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: '#495057' }}>
                  현재 비밀번호
                </label>
                <input
                  type="password"
                  value={passwordForm.oldPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, oldPassword: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  disabled={passwordLoading}
                  autoComplete="current-password"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: '#495057' }}>
                  새 비밀번호
                </label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  maxLength={20}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  disabled={passwordLoading}
                  autoComplete="new-password"
                />
                <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
                  9~20자, 영대문자·영소문자·숫자·특수문자 중 3종류 이상 포함 (현재 {passwordForm.newPassword.length}자)
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: '#495057' }}>
                  새 비밀번호 확인
                </label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  maxLength={20}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #ced4da',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                  disabled={passwordLoading}
                  autoComplete="new-password"
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closePasswordModal}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#495057'
                  }}
                  disabled={passwordLoading}
                >
                  취소
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#4263eb',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: passwordLoading ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    color: 'white',
                    opacity: passwordLoading ? 0.7 : 1
                  }}
                  disabled={passwordLoading}
                >
                  {passwordLoading ? '변경 중...' : '변경하기'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TenantHeader; 